// NameTag.tsx — chip rendering a role or scope name. Outline color is derived
// from a hash of the name and tuned by the common `palette` solver so it stays
// readable against the current theme's bg + card-surface. Role chips get a
// solid horizontal stripe across the bottom (in --color-text-primary, so it
// flips white-on-dark / black-on-light) to differentiate them visually from
// scope chips. The `actions` prop attaches one or more inset icon buttons to
// the right of the name, joined inside the same border by a 1.5px divider —
// used for chip-level operations (delete, edit, etc.).
import { useMemo, type CSSProperties, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { palette } from 'common'

type Kind = 'role' | 'scope'

export type ActionTone = 'danger' | 'warning' | 'success' | 'info' | 'primary'

export interface NameTagAction {
    icon: ReactNode
    onClick: () => void
    label: string
    // Semantic tone for the action's slot (background + icon color). Default
    // is no tint — the icon inherits the chip's hashed outline color over a
    // transparent background.
    tone?: ActionTone
}

// Apply-on-update inline editing. `onChange` fires per-keystroke (color tracks
// live), `onCommit` fires on blur/Enter so the parent can persist.
export interface NameTagEdit {
    onChange: (value: string) => void
    onCommit: () => void
    placeholder?: string
    minWidth?: number
}

interface Props {
    name: string
    kind: Kind
    to?: string
    title?: string
    actions?: NameTagAction[]
    editable?: NameTagEdit
}

const hashHue = (s: string): number => {
    let h = 5381
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i)
    return ((h % 360) + 360) % 360
}

const parseHexVar = (cssVar: string, fallback: string): { r: number; g: number; b: number } => {
    const v = (typeof window !== 'undefined'
        ? getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim()
        : '') || fallback
    const hex = v.startsWith('#') ? v.slice(1) : v
    const full = hex.length === 3
        ? hex.split('').map((c) => c + c).join('')
        : hex
    return {
        r: parseInt(full.slice(0, 2), 16) / 255,
        g: parseInt(full.slice(2, 4), 16) / 255,
        b: parseInt(full.slice(4, 6), 16) / 255,
    }
}

const themeKey = (): string => {
    if (typeof document === 'undefined') return 'ssr'
    return document.documentElement.getAttribute('data-bs-theme')
        || document.documentElement.getAttribute('data-theme')
        || 'default'
}

const colorCache = new Map<string, string>()

const colorForName = (name: string): string => {
    const key = `${themeKey()}::${name}`
    const hit = colorCache.get(key)
    if (hit) return hit
    const bg = parseHexVar('--color-bg-base', '#ffffff')
    const surface = parseHexVar('--color-bg-card', '#ffffff')
    const oklch = palette.solveContrastColor(hashHue(name), bg, surface)
    const hex = palette.oklchToHex(oklch)
    colorCache.set(key, hex)
    return hex
}

export const NameTag = ({ name, kind, to, title, actions, editable }: Props) => {
    const color = useMemo(() => colorForName(name), [name])
    const colorTransition = 'color 600ms ease-out, border-color 600ms ease-out, background-color 600ms ease-out'

    const wrapperStyle: CSSProperties = {
        display: 'inline-flex',
        alignItems: 'stretch',
        border: `1.5px solid ${color}`,
        borderRadius: 4,
        color,
        fontFamily: 'var(--bs-font-monospace, monospace)',
        fontSize: '0.875em',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        transition: colorTransition,
    }

    const namePartStyle: CSSProperties = {
        display: 'inline-block',
        position: 'relative',
        padding: '2px 8px 4px 8px',
        color,
        textDecoration: 'none',
        transition: colorTransition,
    }

    const stripeStyle: CSSProperties = {
        position: 'absolute',
        left: 4,
        right: 4,
        bottom: 1,
        height: 2,
        background: 'var(--color-text-primary, #ffffff)',
        borderRadius: 1,
        pointerEvents: 'none',
    }

    const actionStyle = (tone?: ActionTone): CSSProperties => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tone ? `var(--bs-${tone})` : 'transparent',
        border: 'none',
        borderLeft: `1.5px solid ${color}`,
        color: tone ? '#ffffff' : color,
        padding: '0 6px',
        cursor: 'pointer',
        lineHeight: 1,
        transition: colorTransition,
    })

    let namePart: ReactNode
    if (editable) {
        const minW = editable.minWidth ?? 1
        const measured = name.length || editable.placeholder?.length || minW
        const effective = Math.max(measured, minW)
        const inputStyle: CSSProperties = {
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'inherit',
            font: 'inherit',
            padding: 0,
            margin: 0,
            minWidth: 0,
            width: `${effective}ch`,
        }
        namePart = (
            <span style={namePartStyle} title={title || name}>
                <input
                    type="text"
                    value={name}
                    placeholder={editable.placeholder}
                    onChange={(e) => editable.onChange(e.target.value)}
                    onBlur={() => editable.onCommit()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                                ; (e.currentTarget as HTMLInputElement).blur()
                        }
                    }}
                    style={inputStyle}
                />
                {kind === 'role' && <span style={stripeStyle} />}
            </span>
        )
    } else {
        const nameInner = (
            <>
                {name}
                {kind === 'role' && <span style={stripeStyle} />}
            </>
        )
        namePart = to ? (
            <Link to={to} style={namePartStyle} title={title || name}>{nameInner}</Link>
        ) : (
            <span style={namePartStyle} title={title || name}>{nameInner}</span>
        )
    }

    return (
        <span style={wrapperStyle}>
            {namePart}
            {actions?.map((action, i) => (
                <button
                    key={i}
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); action.onClick() }}
                    style={actionStyle(action.tone)}
                    aria-label={action.label}
                    title={action.label}
                >
                    {action.icon}
                </button>
            ))}
        </span>
    )
}

export default NameTag
